import { cx } from 'emotion';
import { m } from 'framer-motion';
import { Fragment, ReactNode, Suspense } from 'react';
import { useSelector } from 'react-redux';

import { SIZES } from '@ezugi/constants';
import { DeviceInfo } from '@ezugi/utils';

import ChipSelector from '../../../components/ChipSelector';
import { chipsSelector, noBackgroundToolbarSelector } from '../../../store/selectors';
import { BetToolbar } from '../BetToolbar';
import {
  betActionsContainer,
  mobileContainer,
  mobileChipSelector,
  noBackgroundClass,
  clearClassName,
  doubleClassName,
  rebetClassName,
  undoClassName,
  verticalContainer,
  offsetReducedSpaceChipSelector,
  offsetVerticalChipSelector,
} from './BetActions.css';

export type BetActionsProps = Readonly<{
  vertical?: boolean;
  className?: string;
  children?: ReactNode;
  disableRebetOverride?: boolean;
  disableDoubleOverride?: boolean;
  disableUndoOverride?: boolean;
  disableClearOverride?: boolean;
}>;

const animationConfig = DeviceInfo.isHandheld ? {
  initial: { opacity: 0, marginBottom: -SIZES.MOBILE_BET_TOOLBAR_SIZE },
  animate: { opacity: 1, marginBottom: 0 },
  exit: { opacity: 0, marginBottom: -SIZES.MOBILE_BET_TOOLBAR_SIZE },
} : {};

function BetActions({
  className,
  children,
  vertical = false,
  disableRebetOverride,
  disableDoubleOverride,
  disableUndoOverride,
  disableClearOverride,
}: BetActionsProps) {
  const noBackground = useSelector(noBackgroundToolbarSelector) as boolean;

  // prevent overlap with burger menu on 3.0 games for small res devices in landscape
  const chips = useSelector(chipsSelector);
  const hasReducedSpace = vertical && chips.length > 6;

  return (
    <>
      <m.div
        key="bet-toolbar"
        data-e2e="bet-toolbar"
        data-testid="bet-toolbar"
        className={cx(
          betActionsContainer,
          {
            [mobileContainer]: DeviceInfo.isHandheld,
            [verticalContainer]: vertical,
            [noBackgroundClass]: noBackground,
          },
          className
        )}
        {...animationConfig}
      >
        <Fragment>
          <BetToolbar.Double
            disabled={disableDoubleOverride}
            className={doubleClassName}
            hasLabel={true}
          />
          <BetToolbar.Rebet
            disabled={disableRebetOverride}
            className={rebetClassName}
            hasLabel={true}
          />
        </Fragment>

        {DeviceInfo.isHandheld && (
          <Suspense fallback={null}>
            <ChipSelector
              radiusScale={0.95}
              radial={true}
              vertical={vertical}
              className={cx({
                [mobileChipSelector]: true,
                [offsetVerticalChipSelector]: vertical,
                [offsetReducedSpaceChipSelector]: hasReducedSpace,
              })}
            />
          </Suspense>
        )}
        <BetToolbar.Clear
          disabled={disableClearOverride}
          hasLabel={vertical}
          className={clearClassName}
        />
        <BetToolbar.Undo
          disabled={disableUndoOverride}
          hasLabel={true}
          className={undoClassName}
        />
      </m.div>
      <div id="bet-toolbar-extras">{children}</div>
    </>
  );
}

export default BetActions;
